import ReferralInput from '@/components/_referrals/ReferralInput'
import { Button } from '@/components/_ui/Button'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import T, { useTranslate } from '@/components/Helpers/Translate'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'
import { cn } from '@/lib/utils'
import { Combobox } from '@/components/_ui/Combobox'
import { getLanguagesByAlpha2CountryCode } from '@/lib/utils/localization/getLanguagesByAlpha2CountryCode'
import { useHandleChangeLocale } from '@/lib/hooks/helpers/useHandleChangeLocale'
import { isSupportedLanguage } from '@/lib/utils/localization/isSupportedLanguage'
import { languageTranslations } from '@/constants/pickerRegions/regions'
import { Skeleton } from '@/components/_ui/Skeleton'
import { useDevice } from '@/lib/hooks/helpers/useDevice'
import AccountDrawer from '../_shared/AccountDrawer'
import { CartBadge } from '../_shared/CartBadge'
import { HeaderSearchBar } from '../_shared/HeaderSearchBar'
import LoginPopover from '../_shared/LoginPopover'
import ReferredByPopover from '../_shared/ReferredByPopover'
import { MobileDrawer } from './MobileDrawer'
import { MobileHeaderQuickLinks } from './MobileHeaderQuickLinks'

const MobileHeader = () => {
  const { isMobile } = useDevice()
  const {
    user: { refName, refID, isSignedIn },
    app: { isSearchBarActive, language, alpha2 },
    setApp,
  } = useGlobalStore()
  const { gtm } = useGTM()

  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()

  const { isFocusMode, isLoadingIsFocusMode } = useFocusMode()

  const { translate } = useTranslate()

  const { handleChangeLocale } = useHandleChangeLocale()

  const languagesForCurrentCountry = getLanguagesByAlpha2CountryCode(
    alpha2,
  ).map((lang) => ({
    value: lang,
    label: languageTranslations[lang],
    displayLabel: lang.toUpperCase(),
  }))

  const displayCorrectReferralCode = () => {
    if (isSignedIn) return null

    if (refID && refName) {
      return (
        <div className="flex justify-center bg-blue-100 p-2">
          <span className="px-1">
            <T>referred_by</T>
          </span>
          <span className="mr-2 font-bold">{refName}</span>
          <ReferredByPopover />
        </div>
      )
    }

    return (
      <div className="flex justify-center bg-blue-100 p-2">
        <div className="w-[400px]">
          <ReferralInput />
        </div>
      </div>
    )
  }

  if (!isMobile) return null

  return (
    <>
      <MobileDrawer />
      {displayCorrectReferralCode()}
      <div className="bg-white">
        <div className="grid grid-flow-col grid-cols-3 items-center p-4 align-middle">
          {!isFocusMode && !isLoadingIsFocusMode && (
            <div className="align-center flex justify-start">
              <Button
                onClick={() => setApp({ isMobileDrawerOpen: true })}
                size="icon"
                variant="icon"
              >
                <Image
                  alt="Menu"
                  src="/icons/menu.svg"
                  width={25}
                  height={25}
                />
              </Button>
            </div>
          )}

          {isLoadingIsFocusMode && (
            <div className="flex gap-4">
              <Skeleton className="h-[36px] w-[25px] rounded-sm" />
              <Skeleton className="h-[36px] w-[60px] rounded-sm" />
            </div>
          )}

          {isFocusMode && (
            <div className="flex gap-4">
              <Image
                src={`/flags/${alpha2}.svg`}
                alt={`${translate(alpha2)} country flag`}
                height={25}
                width={25}
              />

              <Combobox
                items={languagesForCurrentCountry}
                value={language}
                // placeholder={translate('select_country')}
                // emptyResultsMessage={translate('country_not_found')}
                className="h-auto w-auto justify-start border-0 p-2"
                onChange={async (currentValue) => {
                  const currentValueLowercased = currentValue.toLowerCase()

                  if (!isSupportedLanguage(currentValueLowercased)) return

                  await handleChangeLocale({
                    newAlpha2: alpha2,
                    newLanguage: currentValueLowercased,
                    isRedirectToHome: false,
                  })
                }}
              />
            </div>
          )}

          <a
            href={
              !isFocusMode ? parseLinkUrlWithLocale({ url: '/' }) : undefined
            }
            className={cn(
              'flex items-center justify-center',
              isFocusMode && 'pointer-events-none cursor-default text-gray-400',
            )}
          >
            <Image alt="Unicity" src="/unicity.svg" width={100} height={25} />
          </a>

          <div className="flex justify-end space-x-1">
            {!isFocusMode && !isLoadingIsFocusMode && (
              <Button
                onClick={() => {
                  setApp({ isSearchBarActive: true })
                  gtm.navbarSearchClicked()

                  mixpanelService
                    .trackEvent(MixpanelEvent.NAVIGATION_BAR_SEARCH_CLICKED, {})
                    .catch(() => {})
                }}
                className="pointer-events-auto"
                size="icon"
                variant="icon"
              >
                <Image
                  src="/icons/search.svg"
                  alt="Search"
                  height={25}
                  width={25}
                />
              </Button>
            )}

            {isSignedIn && <AccountDrawer size={30} isMobile />}
            {/* Don't show login popover if signed out, if in focus mode or if loading focus mode */}
            {!isSignedIn && !isFocusMode && !isLoadingIsFocusMode && (
              <LoginPopover isMobile />
            )}

            <Button
              onClick={() => {
                setApp({ isCartOpen: true })
              }}
              size="icon"
              variant="icon"
            >
              <Image
                src="/icons/empty-cart.svg"
                alt="Cart"
                height={25}
                width={25}
              />
              <CartBadge />
            </Button>
          </div>
        </div>
        <HeaderSearchBar />
      </div>
      {!isSearchBarActive && <MobileHeaderQuickLinks />}
    </>
  )
}

export default MobileHeader
